import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronLeft, ExternalLink, Reply, Pencil } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const LANGUAGES = [
  { code: "en_US", label: "English (US)" },
  { code: "en", label: "English" },
  { code: "en_GB", label: "English (UK)" },
  { code: "hi", label: "Hindi" },
  { code: "es", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (BR)" },
  { code: "id", label: "Indonesian" },
  { code: "ar", label: "Arabic" },
];

export default function TemplateCreate() {
  const navigate = useNavigate();
  const { name: editName } = useParams();
  const isEdit = Boolean(editName);

  const [name, setName] = useState(editName || "");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState("en_US");

  const [headerFormat, setHeaderFormat] = useState("NONE"); // NONE | TEXT | IMAGE | DOCUMENT
  const [headerText, setHeaderText] = useState("");
  const [headerMedia, setHeaderMedia] = useState(null); // { header_handle, filename, mime_type, size }
  const [uploadingSample, setUploadingSample] = useState(false);

  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");

  const [buttons, setButtons] = useState([]); // {type, text, url?, phone_number?, url_sample?}
  const [saving, setSaving] = useState(false);
  const [headerSamples, setHeaderSamples] = useState({});
  const [bodySamples, setBodySamples] = useState({});

  const bodyPlaceholders = useMemo(
    () => [...bodyText.matchAll(/{{\s*(\d+)\s*}}/g)].map((m) => m[1]),
    [bodyText]
  );
  const headerPlaceholders = useMemo(
    () => (headerFormat === "TEXT" ? [...headerText.matchAll(/{{\s*(\d+)\s*}}/g)].map((m) => m[1]) : []),
    [headerText, headerFormat]
  );

  const addButton = (type) => {
    if (buttons.length >= 10) return toast.error("Maximum 10 buttons");
    const base = { type };
    if (type === "URL") base.url = "https://";
    if (type === "PHONE_NUMBER") base.phone_number = "+91";
    setButtons([...buttons, { ...base, text: "" }]);
  };

  const updateButton = (idx, patch) =>
    setButtons(buttons.map((b, i) => (i === idx ? { ...b, ...patch } : b)));

  const removeButton = (idx) => setButtons(buttons.filter((_, i) => i !== idx));

  const buildComponents = () => {
    const components = [];
    if (headerFormat === "TEXT" && headerText.trim()) {
      const c = { type: "HEADER", format: "TEXT", text: headerText.trim() };
      if (headerPlaceholders.length) {
        c.example = {
          header_text: headerPlaceholders.map((p, i) => (headerSamples[p] || "").trim() || `Sample ${i + 1}`),
        };
      }
      components.push(c);
    }
    if ((headerFormat === "IMAGE" || headerFormat === "DOCUMENT") && headerMedia?.header_handle) {
      components.push({
        type: "HEADER",
        format: headerFormat,
        example: { header_handle: [headerMedia.header_handle] },
      });
    }
    if (bodyText.trim()) {
      const c = { type: "BODY", text: bodyText.trim() };
      if (bodyPlaceholders.length) {
        c.example = {
          body_text: [bodyPlaceholders.map((p, i) => (bodySamples[p] || "").trim() || `Sample ${i + 1}`)],
        };
      }
      components.push(c);
    }
    if (footerText.trim()) components.push({ type: "FOOTER", text: footerText.trim() });
    if (buttons.length)
      components.push({
        type: "BUTTONS",
        buttons: buttons.map((b) => {
          const btn = { type: b.type, text: b.text };
          if (b.type === "URL") {
            btn.url = b.url;
            if (b.url && /{{\s*\d+\s*}}/.test(b.url)) {
              btn.example = [(b.url_sample || "").trim() || "sample-suffix"];
            }
          }
          if (b.type === "PHONE_NUMBER") btn.phone_number = b.phone_number;
          return btn;
        }),
      });
    return components;
  };

  const submit = async () => {
    const cleanName = name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
    if (!cleanName) return toast.error("Enter a valid template name (a-z, 0-9, _)");
    if (!bodyText.trim()) return toast.error("Body is required");
    if ((headerFormat === "IMAGE" || headerFormat === "DOCUMENT") && !headerMedia?.header_handle) {
      return toast.error(`Upload a sample ${headerFormat.toLowerCase()} first`);
    }
    if (buttons.some((b) => !b.text.trim())) return toast.error("Every button needs a label");
    if (buttons.some((b) => b.type === "URL" && !/^https?:\/\//.test(b.url || ""))) return toast.error("URL buttons need a valid URL");
    if (buttons.some((b) => b.type === "PHONE_NUMBER" && !/^\+\d{6,15}$/.test(b.phone_number || ""))) return toast.error("Phone buttons need a valid E.164 number");

    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/templates/${cleanName}`, {
          category,
          components: buildComponents(),
        });
        toast.success("Template edit submitted to Meta — status is now PENDING re-approval");
      } else {
        await api.post("/templates", {
          name: cleanName,
          language,
          category,
          components: buildComponents(),
        });
        toast.success("Template submitted to Meta for approval");
      }
      navigate("/templates");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEdit ? "Failed to edit template" : "Failed to create template"));
    } finally {
      setSaving(false);
    }
  };

  // Prefill on edit
  useEffect(() => {
    if (!isEdit) return;
    api.get("/templates").then((r) => {
      const t = (r.data || []).find((x) => x.name === editName);
      if (!t) { toast.error("Template not found"); navigate("/templates"); return; }
      setCategory((t.category || "UTILITY").toUpperCase());
      setLanguage(t.language || "en_US");
      const comps = t.components || [];
      const header = comps.find((c) => (c.type || "").toUpperCase() === "HEADER");
      if (header) {
        const fmt = (header.format || "TEXT").toUpperCase();
        if (fmt === "TEXT") {
          setHeaderFormat("TEXT");
          setHeaderText(header.text || "");
        } else if (fmt === "IMAGE" || fmt === "DOCUMENT") {
          setHeaderFormat(fmt);
          const handle = header?.example?.header_handle?.[0];
          if (handle) setHeaderMedia({ header_handle: handle, filename: "(saved sample)" });
        }
      }
      const body = comps.find((c) => (c.type || "").toUpperCase() === "BODY");
      if (body) setBodyText(body.text || "");
      const footer = comps.find((c) => (c.type || "").toUpperCase() === "FOOTER");
      if (footer) setFooterText(footer.text || "");
      const btns = (comps.find((c) => (c.type || "").toUpperCase() === "BUTTONS")?.buttons || []).map((b) => ({
        type: (b.type || "QUICK_REPLY").toUpperCase(),
        text: b.text || "",
        url: b.url || "",
        phone_number: b.phone_number || "",
        url_sample: (b.example && b.example[0]) || "",
        payload: b.payload || "",
      }));
      setButtons(btns);
    }).catch(() => toast.error("Failed to load template"));
  }, [isEdit, editName, navigate]);

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[1fr_420px]">
      {/* Form */}
      <div className="max-h-full overflow-y-auto px-10 py-10">
        <button
          onClick={() => navigate("/templates")}
          className="mb-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
          data-testid="back-to-templates"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Templates
        </button>
        <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">{isEdit ? "Edit template" : "Create template"}</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          {isEdit
            ? "Change the body, header, footer or buttons. Meta re-reviews the template — status resets to PENDING."
            : "Build the template, add optional header, footer and up to 10 buttons, then submit to Meta for approval."}
        </p>

        <section className="mt-10 space-y-6 border border-border p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="e.g. patient_login_otp_t"
                className="mt-1 rounded-sm font-mono"
                data-testid="tpl-name-input"
              />
              <p className="mt-1 text-xs text-muted-foreground">Lowercase letters, numbers and underscores only.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em]">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="tpl-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Category</Label>
            <RadioGroup value={category} onValueChange={setCategory} className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3" data-testid="tpl-category">
              {[
                { v: "MARKETING", d: "Promotions, offers, campaigns" },
                { v: "UTILITY", d: "Order updates, account alerts, reminders" },
                { v: "AUTHENTICATION", d: "One-time passcodes, verification" },
              ].map((c) => (
                <label
                  key={c.v}
                  htmlFor={`cat-${c.v}`}
                  className={`flex cursor-pointer flex-col gap-1 border p-4 transition-colors ${
                    category === c.v ? "border-primary bg-secondary" : "border-border hover:bg-secondary/60"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id={`cat-${c.v}`} value={c.v} />
                    <span className="text-sm font-bold">{c.v}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{c.d}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
        </section>

        <Accordion type="multiple" defaultValue={["body", "buttons"]} className="mt-6">
          <AccordionItem value="header" className="border-border">
            <AccordionTrigger className="font-display font-bold" data-testid="acc-header">Header (optional)</AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <Select value={headerFormat} onValueChange={(v) => { setHeaderFormat(v); setHeaderMedia(null); }}>
                <SelectTrigger className="w-56 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  <SelectItem value="TEXT">Text</SelectItem>
                  <SelectItem value="IMAGE">Image (JPG/PNG)</SelectItem>
                  <SelectItem value="DOCUMENT">Document (PDF)</SelectItem>
                </SelectContent>
              </Select>
              {headerFormat === "TEXT" && (
                <Input
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  maxLength={60}
                  placeholder="Login verification"
                  className="rounded-sm"
                  data-testid="tpl-header-input"
                />
              )}
              {(headerFormat === "IMAGE" || headerFormat === "DOCUMENT") && (
                <div className="space-y-2 border border-dashed border-border bg-secondary/40 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Sample {headerFormat === "DOCUMENT" ? "PDF" : "image"} · uploaded to Meta for approval only
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Meta needs a representative sample so reviewers can see what header media looks like. When you send this template, you'll attach the real file (per customer).
                  </p>
                  <input
                    type="file"
                    accept={headerFormat === "DOCUMENT" ? "application/pdf" : "image/jpeg,image/png"}
                    disabled={uploadingSample}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      setUploadingSample(true);
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await api.post("/templates/upload-sample", fd, {
                          headers: { "Content-Type": "multipart/form-data" },
                        });
                        setHeaderMedia(r.data);
                        toast.success(`Uploaded ${r.data.filename}`);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Upload failed");
                      } finally {
                        setUploadingSample(false);
                      }
                    }}
                    data-testid="tpl-header-media-input"
                    className="block w-full text-xs"
                  />
                  {uploadingSample && <div className="text-[11px] text-muted-foreground">Uploading to Meta…</div>}
                  {headerMedia?.header_handle && (
                    <div className="flex items-center justify-between gap-2 border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-300" data-testid="tpl-header-media-ok">
                      <div className="truncate">
                        ✓ {headerMedia.filename}
                        {headerMedia.size ? <span className="ml-2 text-muted-foreground">({Math.round(headerMedia.size/1024)} KB)</span> : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => setHeaderMedia(null)}
                        className="text-xs underline hover:no-underline"
                      >
                        remove
                      </button>
                    </div>
                  )}
                </div>
              )}
              {headerFormat === "TEXT" && headerPlaceholders.length > 0 && (
                <div className="border border-dashed border-border bg-secondary/40 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Header variable samples · sent to Meta for approval only
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Meta requires separate samples for header vs body — this section covers header variables only.
                  </p>
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                    {headerPlaceholders.map((p) => (
                      <div key={`hs-${p}`}>
                        <Label className="font-mono text-[10px] text-muted-foreground">Header · {`{{${p}}}`}</Label>
                        <Input
                          value={headerSamples[p] || ""}
                          onChange={(e) => setHeaderSamples((s) => ({ ...s, [p]: e.target.value }))}
                          placeholder={`Header sample ${p}`}
                          className="mt-0.5 rounded-sm"
                          data-testid={`tpl-header-sample-${p}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="body" className="border-border">
            <AccordionTrigger className="font-display font-bold" data-testid="acc-body">Body <span className="ml-2 text-xs text-red-500">*</span></AccordionTrigger>
            <AccordionContent className="space-y-2 pt-2">
              <Textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                maxLength={1024}
                rows={5}
                placeholder="Your OTP is {{1}}. It expires in 10 minutes."
                className="rounded-sm"
                data-testid="tpl-body-input"
              />
              <div className="text-xs text-muted-foreground">
                {bodyText.length}/1024 · Use <code className="rounded bg-secondary px-1 font-mono">{"{{1}}"}, {"{{2}}"}</code> for variables. Detected: {bodyPlaceholders.length}
              </div>
              {bodyPlaceholders.length > 0 && (
                <div className="border border-dashed border-border bg-secondary/40 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Body variable samples · realistic examples improve Meta approval odds
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Meta requires separate samples for header vs body — this section covers body variables only.
                  </p>
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                    {bodyPlaceholders.map((p) => (
                      <div key={`bs-${p}`}>
                        <Label className="font-mono text-[10px] text-muted-foreground">Body · {`{{${p}}}`}</Label>
                        <Input
                          value={bodySamples[p] || ""}
                          onChange={(e) => setBodySamples((s) => ({ ...s, [p]: e.target.value }))}
                          placeholder={`Body sample ${p}`}
                          className="mt-0.5 rounded-sm"
                          data-testid={`tpl-body-sample-${p}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-muted-foreground">
                    These are only shown to Meta reviewers. Real values are supplied at send time.
                  </p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="footer" className="border-border">
            <AccordionTrigger className="font-display font-bold" data-testid="acc-footer">Footer (optional)</AccordionTrigger>
            <AccordionContent className="pt-2">
              <Input
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                maxLength={60}
                placeholder="Do not share this code."
                className="rounded-sm"
                data-testid="tpl-footer-input"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="buttons" className="border-border">
            <AccordionTrigger className="font-display font-bold" data-testid="acc-buttons">Buttons (up to 10)</AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addButton("QUICK_REPLY")} className="rounded-sm" data-testid="add-quick-reply-btn">
                  <Reply className="mr-1 h-3.5 w-3.5" /> Quick reply
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addButton("URL")} className="rounded-sm" data-testid="add-url-btn">
                  <ExternalLink className="mr-1 h-3.5 w-3.5" /> URL
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addButton("PHONE_NUMBER")} className="rounded-sm" data-testid="add-phone-btn">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Phone
                </Button>
                <span className="ml-auto self-center text-xs text-muted-foreground">{buttons.length}/10</span>
              </div>
              <div className="space-y-2">
                {buttons.map((b, i) => (
                  <div key={i} className="grid grid-cols-1 gap-2 border border-border p-3 md:grid-cols-[110px_1fr_1fr_auto]">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground self-center">{b.type.replace("_", " ")}</div>
                    <Input
                      value={b.text}
                      maxLength={25}
                      onChange={(e) => updateButton(i, { text: e.target.value })}
                      placeholder="Button label"
                      className="rounded-sm"
                      data-testid={`btn-text-${i}`}
                    />
                    {b.type === "URL" && (
                      <Input
                        value={b.url}
                        onChange={(e) => updateButton(i, { url: e.target.value })}
                        placeholder="https://example.com/help?ref={{1}}"
                        className="rounded-sm font-mono text-xs"
                        data-testid={`btn-url-${i}`}
                      />
                    )}
                    {b.type === "URL" && /{{\s*\d+\s*}}/.test(b.url || "") && (
                      <Input
                        value={b.url_sample || ""}
                        onChange={(e) => updateButton(i, { url_sample: e.target.value })}
                        placeholder="sample-suffix (for Meta review)"
                        className="rounded-sm font-mono text-xs md:col-start-3"
                        data-testid={`btn-url-sample-${i}`}
                      />
                    )}
                    {b.type === "PHONE_NUMBER" && (
                      <Input
                        value={b.phone_number}
                        onChange={(e) => updateButton(i, { phone_number: e.target.value })}
                        placeholder="+919999999999"
                        className="rounded-sm font-mono text-xs"
                        data-testid={`btn-phone-${i}`}
                      />
                    )}
                    {b.type === "QUICK_REPLY" && (
                      <Input
                        value={b.payload || ""}
                        onChange={(e) => updateButton(i, { payload: e.target.value })}
                        placeholder="Payload (optional, e.g. confirm | reschedule | cancel)"
                        className="rounded-sm font-mono text-xs"
                        data-testid={`btn-payload-${i}`}
                      />
                    )}
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeButton(i)} aria-label="Remove" data-testid={`btn-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="mt-8 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={() => navigate("/templates")} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="submit-template-btn">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Submitting…" : "Submit to Meta"}
          </Button>
        </div>
      </div>

      {/* Preview */}
      <aside className="sticky top-0 hidden h-full border-l border-border bg-secondary lg:block">
        <div className="border-b border-border p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Live Preview</div>
          <div className="mt-1 font-display font-bold">WhatsApp bubble</div>
        </div>
        <div className="wa-preview-bg h-[calc(100%-73px)] overflow-y-auto p-6">
          <div className="max-w-sm wa-bubble-in shadow-sm">
            {headerFormat === "TEXT" && headerText && (
              <div className="px-3 pt-2 text-sm font-bold text-neutral-900">{headerText}</div>
            )}
            {(headerFormat === "IMAGE") && (
              <div className="m-2 flex h-28 items-center justify-center bg-neutral-200 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
                [ IMAGE HEADER ]
              </div>
            )}
            {(headerFormat === "DOCUMENT") && (
              <div className="m-2 flex items-center gap-2 border border-neutral-300 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
                <span className="inline-block h-6 w-5 bg-red-500 text-center text-[9px] font-bold leading-6 text-white">PDF</span>
                <span className="truncate">{headerMedia?.filename || "document.pdf"}</span>
              </div>
            )}
            <div className="whitespace-pre-wrap px-3 py-2 text-sm text-neutral-900">
              {bodyText || <span className="text-neutral-400">Body preview…</span>}
            </div>
            {footerText && <div className="px-3 pb-2 text-xs text-neutral-500">{footerText}</div>}
            {buttons.length > 0 && (
              <div className="border-t border-neutral-200">
                {buttons.map((b, i) => (
                  <div key={i} className="border-b border-neutral-200 py-2 text-center text-sm font-medium text-[#00a5f4] last:border-b-0">
                    {b.text || <span className="text-neutral-400">Button {i + 1}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
